import React, { useState } from 'react';
import classNames from 'classnames';

import { useOverlayState } from '../../../../hooks/useOverlayState';

import EyeIcon from '../../../../design-system/Icons/EyeIcon';
import EditLineIcon from '../../../../design-system/Icons/EditLineIcon';
import EditCycleStagePanel from '../EditCycleStagePanel/EditCycleStagePanel';
import { Badge, ButtonIcon, Toggle } from '../../../../design-system';

const StageCard = ({
    stage,
    setStages,
    isDefault = false,
    isDeleteAvailable = false,
    makeStageDefault,
    cycleId,
}) => {
    const { id, order, name, description, stage_status, is_deleted: isArchived } = stage;
    const { status_label, status_color } = stage_status || {};

    const { isOpened, open: openPanel, close: closePanel } = useOverlayState();

    const [stageFullData, setStageFullData] = useState(null);

    const handleToggleChange = async () => {
        if (isDefault || isArchived) return;

        await makeStageDefault(id);
    };

    const setStage = (cb) =>
        setStages((stages) => {
            return stages.map((stage) => {
                if (stage.id === id) {
                    return typeof cb === 'function' ? cb(stage) : cb;
                }
                return stage;
            });
        });

    const renderBadge = () => {
        if (isArchived) {
            return (
                <div className="h-[23px] px-2 flex items-center font-body-medium text-neutral-500 text-body-medium-xs truncate">
                    Archived
                </div>
            );
        }

        return <Badge text={status_label} color={status_color} />;
    };

    const cardClassName = classNames(
        'px-4 py-3 rounded-2 border-1 flex flex-col-reverse md:flex-row md:items-center md:justify-between gap-y-2 gap-x-3 md:gap-x-4 transition-all',
        isArchived ? 'bg-neutral-50 border-transparent' : 'bg-white border-neutral-200 opacity-100'
    );

    const mainInfoClassName = classNames(
        'flex items-center gap-3 overflow-hidden transition-opacity',
        isArchived ? 'opacity-50' : 'opacity-100'
    );

    const actionsClassName = classNames(
        'flex items-center justify-between md:justify-end gap-2 lg:gap-4 transition-opacity',
        isArchived ? 'opacity-50' : 'opacity-100'
    );

    return (
        <div className={cardClassName}>
            <div className={mainInfoClassName}>
                <Toggle
                    size="sm"
                    name="is_default"
                    isSelected={isDefault}
                    onChange={handleToggleChange}
                    isDisabled={isArchived}
                />

                <div className="overflow-hidden">
                    <p className="font-body-bold text-body-bold-s text-neutral-500">
                        ({order}) {name}
                    </p>
                    <p className="font-body text-body-regular-xs text-neutral-500 truncate">
                        {description}
                    </p>
                </div>
            </div>

            <div className={actionsClassName}>
                {renderBadge()}

                <div className="flex items-center gap-2">
                    {!isArchived && (
                        <ButtonIcon type="link" size="xs" icon={EditLineIcon} onClick={openPanel} />
                    )}
                    {isArchived && (
                        <ButtonIcon type="link" size="xs" icon={EyeIcon} onClick={openPanel} />
                    )}
                </div>
            </div>

            {isOpened && (
                <EditCycleStagePanel
                    stage={stage}
                    setStage={setStage}
                    isDefault={isDefault}
                    isEditable={!isArchived}
                    isDeleteAvailable={isDeleteAvailable}
                    stageFullData={stageFullData}
                    setStageFullData={setStageFullData}
                    cycleId={cycleId}
                    onClose={closePanel}
                />
            )}
        </div>
    );
};

export default StageCard;
